/* A Druid's Beastform: the form they are in and what it changes on the sheet. Every
   number comes from the Beastform list (data/beastforms.json); evolved and hybrid
   forms read their bonuses and costs from their own feature text. */

import type { Catalog } from './catalog';
import type { Trait } from './model';

type Form = Catalog['beastforms'][number];

export interface ActiveBeastform {
  name: string;
  /** The form an evolved form (Legendary or Mythic Beast) upgrades. */
  base?: string;
  /** The trait Evolution raised by +1, when the Druid spent Hope instead of a Stress. */
  evolution?: Trait;
}

/** The Druid's Hope feature: "Spend 3 Hope to transform into a Beastform without marking a Stress." */
export const EVOLUTION_HOPE = 3;

const featureText = (form: Form | undefined, name: string) => form?.features.find((f) => f.name === name)?.text ?? '';
const bonus = (text: string, what: string) => Number(text.match(new RegExp(`\\+(\\d+) bonus to ${what}`))?.[1] ?? 0);

/** The tiers an evolved form can upgrade ("Pick a Tier 1 or Tier 2 Beastform option"); none for other forms. */
export const evolvedTiers = (form?: Form) =>
  Array.from(featureText(form, 'Evolved').split('.')[0].matchAll(/Tier (\d)/g), (m) => Number(m[1]));

/** The Stress a hybrid adds to the transformation ("mark an additional Stress", "mark 2 additional Stress"). */
export function extraStress(form?: Form) {
  const count = featureText(form, 'Hybrid Features').match(/mark (an|\d+) additional Stress/)?.[1];
  return count === undefined ? 0 : count === 'an' ? 1 : Number(count);
}

const DIE_SIZES = ['d4', 'd6', 'd8', 'd10', 'd12'];
const sizeUp = (die: string) => DIE_SIZES[Math.min(DIE_SIZES.indexOf(die) + 1, DIE_SIZES.length - 1)];

/** What the active form adds, with an evolved form's bonuses on top of the form it upgrades. */
export function formStats(active: ActiveBeastform, catalog: Catalog) {
  const find = (name?: string) => catalog.beastforms.find((f) => f.name === name);
  const form = find(active.name);
  const evolved = featureText(form, 'Evolved');
  const stats = evolved ? find(active.base) : form;
  if (!form || !stats?.trait) return null;
  return {
    name: form.name,
    label: evolved ? `${form.name} (${stats.name})` : form.name,
    trait: stats.trait.toLowerCase() as Trait,
    traitBonus: (stats.traitMod ?? 0) + bonus(evolved, 'the trait'),
    evasionBonus: (stats.evasionMod ?? 0) + bonus(evolved, 'Evasion'),
    range: stats.attackRange ?? '',
    attackTrait: stats.attackTrait ?? '',
    die: /die increases by one size/.test(evolved) ? sizeUp(stats.attackDamage ?? '') : stats.attackDamage ?? '',
    damageBonus: (stats.attackMod ?? 0) + bonus(evolved, 'damage rolls'),
    advantages: stats.advantages,
  };
}

export type FormStats = NonNullable<ReturnType<typeof formStats>>;

/** "Melee Agility 1d4 phy": the form's range, trait and dice, rolled with the character's Proficiency. */
export const attackLine = (f: FormStats, proficiency: number) =>
  `${f.range} ${f.attackTrait} ${proficiency}${f.die}${f.damageBonus ? `+${f.damageBonus}` : ''} phy`;
